import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { EventDiscoveryClient } from 'src/WebApiController';

interface UseGetFilteredNotificationsProps {
  searchText: string;
}

export const useGetFilteredNotifications = ({
  searchText,
}: UseGetFilteredNotificationsProps) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();

  const dataQuery = useQuery({
    queryKey: [
      'EventDiscoveryClient.getEventNotifications',
      activeAccountWebClientConfig.activeAccountId,
    ],
    queryFn: () => {
      if (activeAccountWebClientConfig.activeAccountId == null) {
        return null;
      }
      return tryInvokeApi(
        () => {
          return new EventDiscoveryClient(
            activeAccountWebClientConfig
          ).getEventNotifications();
        },
        (error) => {
          trackError('EventDiscoveryClient.getEventNotifications', error);
        }
      );
    },
    enabled: activeAccountWebClientConfig.activeAccountId != null,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const filteredNotifications = useMemo(() => {
    if (!searchText.trim()) {
      return dataQuery.data;
    }

    const lowerSearchText = searchText.toLowerCase();
    return dataQuery.data?.filter(
      (notification) =>
        notification.name.toLowerCase().includes(lowerSearchText) ||
        notification.status.toLowerCase().includes(lowerSearchText) ||
        notification.notificationType.toLowerCase().includes(lowerSearchText)
    );
  }, [dataQuery, searchText]);

  return { notificationsQuery: dataQuery, filteredNotifications };
};
